import { type SceneKeyframe, phaseAt } from '../scene-clock'

/**
 * Schedule and demo data for the "Apply From Any Device" scene: one device
 * frame morphs phone > tablet > laptop while the five application steps run
 * inside it. Everything here is pure and unit-tested; the component in
 * app/components/site/home/scenes/DeviceMorphScene.tsx only renders it.
 *
 * The names, banks and copy are demo content for the mock screens (from the
 * reference), not product copy, which is why they live here and not in
 * app/utils/site/content.ts.
 */

export type MorphDevice = 'phone' | 'tablet' | 'laptop'
export const MORPH_DEVICES = [
	'phone',
	'tablet',
	'laptop',
] as const satisfies readonly MorphDevice[]

export type MorphStepId =
	| 'household'
	| 'identity'
	| 'rental'
	| 'employment'
	| 'bank'

export interface MorphStep {
	id: MorphStepId
	device: MorphDevice
	/** How long the step stays on screen before the next one slides in. */
	holdMs: number
}

/** 3.2 s per step, 4.2 s on the last so the bank list can be read. */
export const MORPH_STEP_MS = 3200
export const MORPH_LAST_STEP_MS = 4200

export const MORPH_STEPS: readonly MorphStep[] = [
	{ id: 'household', device: 'phone', holdMs: MORPH_STEP_MS },
	{ id: 'identity', device: 'phone', holdMs: MORPH_STEP_MS },
	{ id: 'rental', device: 'tablet', holdMs: MORPH_STEP_MS },
	{ id: 'employment', device: 'tablet', holdMs: MORPH_STEP_MS },
	{ id: 'bank', device: 'laptop', holdMs: MORPH_LAST_STEP_MS },
]

export const MORPH_STEP_COUNT = MORPH_STEPS.length

/** The first step, also what reduced motion shows: step 1 on the phone. */
export const MORPH_FIRST_STEP: MorphStepId = 'household'

/** Keyframes for `useSceneClock`: each step starts when the previous ends. */
export const MORPH_KEYFRAMES: readonly SceneKeyframe<MorphStepId>[] =
	MORPH_STEPS.reduce<SceneKeyframe<MorphStepId>[]>((frames, step) => {
		const previous = frames[frames.length - 1]
		const previousStep = MORPH_STEPS[frames.length - 1]
		const at = previous && previousStep ? previous.at + previousStep.holdMs : 0
		frames.push({ at, phase: step.id })
		return frames
	}, [])

/** One full pass through the five steps; the clock loops after it. */
export const MORPH_LOOP_MS = MORPH_STEPS.reduce(
	(total, step) => total + step.holdMs,
	0,
)

/** Zero-based position of a step in the flow. */
export function stepIndex(id: MorphStepId): number {
	const index = MORPH_STEPS.findIndex((step) => step.id === id)
	if (index < 0) throw new Error(`Unknown morph step: ${id}`)
	return index
}

/** Which step is on screen `elapsedMs` into the loop. */
export function stepAt(elapsedMs: number): MorphStepId {
	return phaseAt(elapsedMs, MORPH_KEYFRAMES, MORPH_LOOP_MS)
}

/** The device the frame takes for a step. */
export function deviceForStep(id: MorphStepId): MorphDevice {
	const step = MORPH_STEPS[stepIndex(id)]
	if (!step) throw new Error(`Unknown morph step: ${id}`)
	return step.device
}

/**
 * The step that just left the screen: steps only ever advance by one (and
 * wrap), so the outgoing screen is always the previous one in the list. It
 * gets the "slide out to the left" state while the current step slides in
 * from the right.
 */
export function previousStep(id: MorphStepId): MorphStepId {
	const index = (stepIndex(id) - 1 + MORPH_STEP_COUNT) % MORPH_STEP_COUNT
	const step = MORPH_STEPS[index]
	if (!step) throw new Error(`Unknown morph step: ${id}`)
	return step.id
}

export type MorphScreenState = 'on' | 'out' | 'off'

/** Entrance state of each screen for the current step. */
export function screenStateFor(
	current: MorphStepId,
	screen: MorphStepId,
): MorphScreenState {
	if (screen === current) return 'on'
	if (screen === previousStep(current)) return 'out'
	return 'off'
}

/** Which of the five progress segments are filled on a step. */
export function progressFor(id: MorphStepId): boolean[] {
	const current = stepIndex(id)
	return MORPH_STEPS.map((_, index) => index <= current)
}

/** The "n of 5" counter in the top bar. */
export function stepLabel(id: MorphStepId): string {
	return `${stepIndex(id) + 1} of ${MORPH_STEP_COUNT}`
}

/* ------------------------------------------------------------------------ */
/* Demo content for the five mock screens (from the reference, verbatim).   */
/* ------------------------------------------------------------------------ */

export type AvatarTone = 'sage' | 'lav'

export interface MorphPerson {
	initials: string
	name: string
	role: string
	tone: AvatarTone
}

export interface MorphBank {
	/** The letters inside the brand-coloured circle. */
	mark: string
	name: string
	domain: string
	/** The bank's brand colour, used only for its circle. */
	colour: string
}

export const MORPH_COPY = {
	wordmarkAlt: 'TrueTenant',
	next: 'Next',
	submit: 'Submit',
	household: {
		title: 'Who’s Moving In?',
		sub: 'Add everyone on the lease.',
		people: [
			{ initials: 'SJ', name: 'Sarah Johnson', role: 'You', tone: 'sage' },
			{ initials: 'DK', name: 'Daniel Kim', role: 'Roommate', tone: 'lav' },
		] satisfies MorphPerson[],
		add: '+ Add a guarantor',
	},
	identity: {
		title: 'Verify Your ID',
		sub: 'Scan your ID, then a quick selfie.',
		securedBy: 'Secured by',
		veriff: { src: '/img/site/veriff.png', alt: 'Veriff' },
	},
	rental: {
		title: 'Rental History',
		sub: 'Select one that applies.',
		options: ['Currently renting', 'Previously rented', 'First-time renter'],
		selected: 0,
	},
	employment: {
		title: 'Employment',
		sub: 'Select one that applies.',
		options: [
			'Currently employed',
			'Not employed at the moment',
			'No employment history',
		],
		selected: 0,
	},
	bank: {
		flinks: { src: '/img/site/flinks.png', alt: 'Flinks' },
		title: 'Who Do You Bank With?',
		search: 'Search financial institutions',
		lock: 'Flinks securely connects your account.',
		banks: [
			{
				mark: 'S',
				name: 'Scotiabank',
				domain: 'scotiabank.com',
				colour: '#EC111A',
			},
			{
				mark: 'RBC',
				name: 'RBC',
				domain: 'rbcroyalbank.com',
				colour: '#005DAA',
			},
			{ mark: 'TD', name: 'TD', domain: 'td.com', colour: '#34A853' },
			{ mark: 'C', name: 'CIBC', domain: 'cibc.com', colour: '#C41F3E' },
		] satisfies MorphBank[],
	},
	tabs: { phone: 'Phone', tablet: 'Tablet', laptop: 'Laptop' } satisfies Record<
		MorphDevice,
		string
	>,
} as const
